<template>
  <div class="border rounded-lg border-blueDianne/20 px-5 py-3 mt-3">
    <div class="flex cursor-pointer" @click="isExpanded = !isExpanded">
      <button class="mr-3 text-blueDianne hover:text-blueDianne-700">
        <i
          :class="
            isExpanded
              ? 'fa-solid fa-chevron-down'
              : 'fa-solid fa-chevron-right'
          "
        ></i>
      </button>
      <h4 class="text-xl">{{ question.question }}</h4>
    </div>
    <div v-if="isExpanded">
      <div class="bg-blueDianne/10 rounded-lg px-3 py-2 mt-3 text-sm">
        <em>Search Term:</em> {{ question.searchTerm }}
      </div>
      <div
        v-if="
          question.minYear ||
          question.maxYear ||
          question.minQuartile ||
          question.openAccessOnly ||
          question.minInfluentialCitationCount ||
          question.minCitationCount
        "
        class="mt-2 bg-blueDianne/10 rounded-lg mb-2 text-sm"
      >
        <div>
          <div class="px-3 pt-2">
            <i class="fa-solid fa-filter text-tiara-400"></i>&nbsp;&nbsp;<em
              >Filtered results:
            </em>
          </div>
          <div class="px-3 py-2">
            <ul class="list-disc ml-5 w-full flex flex-wrap">
              <template v-if="question.minYear || question.maxYear">
                <li
                  v-if="question.minYear && !question.maxYear"
                  class="w-full md:w-1/2"
                >
                  Published from {{ question.minYear }} to present
                </li>
                <li
                  v-else-if="!question.minYear && question.maxYear"
                  class="w-full md:w-1/2"
                >
                  Published before {{ question.maxYear }}
                </li>
                <li
                  v-else-if="question.minYear && question.maxYear"
                  class="w-full md:w-1/2"
                >
                  Published from {{ question.minYear }} until
                  {{ question.maxYear }}
                </li>
              </template>
              <template v-if="question.minQuartile">
                <li class="w-full md:w-1/2">
                  Journal quartile: Q{{ question.minQuartile
                  }}<span v-if="question.minQuartile > 1"> or better</span>
                </li>
              </template>
              <template v-if="question.openAccessOnly">
                <li class="w-full md:w-1/2">Open access only</li>
              </template>
              <template v-if="question.minInfluentialCitationCount">
                <li class="w-full md:w-1/2">
                  Minimum influential citation count:
                  {{ question.minInfluentialCitationCount }}
                </li>
              </template>
              <template v-if="question.minCitationCount">
                <li class="w-full md:w-1/2">
                  Minimum citation count: {{ question.minCitationCount }}
                </li>
              </template>
            </ul>
          </div>
        </div>
      </div>
      <div v-for="answer in question.answers" :key="answer.hashedId">
        <p class="form-label mt-3">{{ answer.answerType?.label }}</p>
        <markdown-viewer :text="answer.content" />
      </div>
      <div class="border-b border-blueDianne my-3 flex text-sm">
        <div
          class="py-2 px-4 uppercase cursor-pointer"
          :class="{
            'border-b-2 border-blueDianne/50': selectedMode === 'sources',
          }"
          @click="selectMode('sources')"
        >
          Sources
        </div>
        <div
          class="py-2 px-4 uppercase cursor-pointer"
          :class="{
            'border-b-2 border-blueDianne/50': selectedMode === 'experts',
          }"
          @click="selectMode('experts')"
        >
          Experts
        </div>
        <div
          v-if="modes.indexOf('patents') > -1"
          class="py-2 px-4 uppercase cursor-pointer"
          :class="{
            'border-b-2 border-blueDianne/50': selectedMode === 'patents',
          }"
          @click="selectMode('patents')"
        >
          Patents
        </div>
        <div
          v-if="modes.indexOf('industryPartners') > -1"
          class="py-2 px-4 uppercase cursor-pointer"
          :class="{
            'border-b-2 border-blueDianne/50':
              selectedMode === 'industryPartners',
          }"
          @click="selectMode('industryPartners')"
        >
          Industry Partners
        </div>
      </div>
      <div>
        <div v-if="selectedMode === 'sources'">
          <div class="bg-blueDianne/10 px-4 rounded-lg mb-3">
            <div class="flex pb-2">
              <div class="w-full md:w-1/2">
                <p class="form-label mt-3">Status</p>
                {{ question.sourcesStatus.message }}
              </div>
              <div class="w-full md:w-1/2">
                <p class="form-label mt-3">Duration</p>
                {{
                  question.sourcesStatus.completedAt
                    ? (new Date(question.sourcesStatus.completedAt).getTime() -
                        new Date(question.sourcesStatus.beganAt).getTime()) /
                        1000 +
                      " seconds"
                    : "In Progress"
                }}
              </div>
            </div>
          </div>
          <div
            v-for="source in question.sources"
            :key="source.id"
            class="bg-white border-blueDianne-20 rounded-lg px-4 py-3 mb-3"
          >
            <h4 class="text-xl mb-3">{{ source.metadata.title }}</h4>
            <div v-if="source.metadata.bibtexCitation">
              <p class="form-label mt-3">Citation</p>
              <p class="text-sm">
                {{ formattedCitation(source.metadata.bibtexCitation) }}
              </p>
            </div>
            <div v-if="source.metadata.tldr">
              <p class="form-label mt-3">TLDR</p>
              <p class="text-sm">{{ source.metadata.tldr }}</p>
            </div>
            <div v-if="source.pageContent">
              <p class="form-label mt-3">Abstract</p>
              <p class="text-sm">{{ source.pageContent }}</p>
            </div>
            <div v-if="source.metadata.contribution">
              <p class="form-label mt-3">Contribution</p>
              <p class="bg-green-500/10 text-sm px-3 py-2 rounded">
                {{ source.metadata.contribution }}
              </p>
            </div>
          </div>
        </div>
        <div v-if="selectedMode === 'experts'">
          <div
            v-if="question.authorsStatus.beganAt"
            class="bg-blueDianne/10 px-4 rounded-lg mb-3"
          >
            <div class="flex pb-2">
              <div class="w-full md:w-1/2">
                <p class="form-label mt-3">Status</p>
                {{ question.authorsStatus.message }}
              </div>
              <div class="w-full md:w-1/2">
                <p class="form-label mt-3">Duration</p>
                {{
                  question.authorsStatus.completedAt
                    ? (new Date(question.authorsStatus.completedAt).getTime() -
                        new Date(question.authorsStatus.beganAt).getTime()) /
                        1000 +
                      " seconds"
                    : "In Progress"
                }}
              </div>
            </div>
          </div>
          <div v-else>
            <div class="bg-blueDianne/10 rounded-lg px-4 py-3 mb-3">
              <p>
                Expert panel was not loaded for this question but base expert
                information is loaded with sources.
              </p>
            </div>
          </div>
          <div
            v-for="author in orderedAuthors"
            :key="author.authorId"
            class="bg-white border-blueDianne-20 rounded-lg px-4 py-3 mb-3"
          >
            <research-compass-question-author :author="author" />
          </div>
        </div>
        <div v-if="selectedMode === 'patents'">
          <div
            v-if="question.patentsStatus.beganAt"
            class="bg-blueDianne/10 px-4 rounded-lg mb-3"
          >
            <div class="flex pb-2">
              <div class="w-full md:w-1/2">
                <p class="form-label mt-3">Status</p>
                {{ question.patentsStatus.message }}
              </div>
              <div class="w-full md:w-1/2">
                <p class="form-label mt-3">Duration</p>
                {{
                  question.patentsStatus.completedAt
                    ? (new Date(question.patentsStatus.completedAt).getTime() -
                        new Date(question.patentsStatus.beganAt).getTime()) /
                        1000 +
                      " seconds"
                    : "In Progress"
                }}
              </div>
            </div>
          </div>
          <div v-else>
            <div class="bg-blueDianne/10 rounded-lg px-4 py-3">
              <p>Patent panel was not loaded for this question.</p>
            </div>
          </div>
          <div
            v-for="patent in question.patents.patents"
            :key="patent.patentNumber"
            class="bg-white border-blueDianne-20 rounded-lg px-4 py-3 mb-3"
          >
            {{ patent }}
          </div>
        </div>
        <div v-if="selectedMode === 'industryPartners'">
          <div
            v-if="question.industryPartnersStatus.beganAt"
            class="bg-blueDianne/10 px-4 rounded-lg mb-3"
          >
            <div class="flex pb-2">
              <div class="w-full md:w-1/2">
                <p class="form-label mt-3">Status</p>
                {{ question.industryPartnersStatus.message }}
              </div>
              <div class="w-full md:w-1/2">
                <p class="form-label mt-3">Duration</p>
                {{
                  question.industryPartnersStatus.completedAt
                    ? (new Date(
                        question.industryPartnersStatus.completedAt,
                      ).getTime() -
                        new Date(
                          question.industryPartnersStatus.beganAt,
                        ).getTime()) /
                        1000 +
                      " seconds"
                    : "In Progress"
                }}
              </div>
            </div>
          </div>
          <div v-else>
            <div class="bg-blueDianne/10 rounded-lg px-4 py-3">
              <p>Industry Partners panel was not loaded for this question.</p>
            </div>
          </div>
          <div
            v-for="partner in question.industryPartners"
            :key="partner.name"
            class="bg-white border-blueDianne-20 rounded-lg px-4 py-3 mb-3"
          >
            {{ partner }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { Cite } from "@citation-js/core";
import MarkdownViewer from "../MarkdownViewer.vue";
import ResearchCompassQuestionAuthor from "./ResearchCompassQuestionAuthor.vue";
import "@citation-js/plugin-bibtex";
import "@citation-js/plugin-csl";

export default {
  components: {
    MarkdownViewer,
    ResearchCompassQuestionAuthor,
  },
  props: {
    question: Object,
    modes: Array,
  },
  data() {
    return {
      selectedMode: "sources",
      isExpanded: false,
    };
  },
  computed: {
    orderedAuthors() {
      return Object.values(this.question.authors).sort((a, b) => {
        if (a.normalizedFitScore && b.normalizedFitScore) {
          return b.normalizedFitScore - a.normalizedFitScore;
        }
        if (a.normalizedFitScore) {
          return -1;
        }
        if (b.normalizedFitScore) {
          return 1;
        }
        return 0;
      });
    },
  },
  methods: {
    selectMode(mode) {
      this.selectedMode = mode;
    },
    formattedCitation(bibString) {
      const cite = new Cite(bibString);
      return cite.format("bibliography", { template: "mla" });
    },
  },
};
</script>
